import { INSUFFICIENT_BALANCE_CHANNEL } from '@/service/constants/system'
import { toast } from 'sonner'
import { sendBroadcastChannel } from './utils'

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number>
  data?: any
  skip401Redirect?: boolean
  skipErrorToast?: boolean
}

export interface ApiResponse<T = any> {
  code: number
  data: T
  message: string
}

type RequestError = Error & {
  status?: number
  code?: number
}

export async function request<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
  // Determine which token to use based on the current path or options
  let token: string | null = null
  let admin_token: string | null = null
  let oem_admin_token: string | null = null

  if (typeof window !== 'undefined') {
    token = localStorage.getItem('token')
    admin_token = localStorage.getItem('admin_token')
    oem_admin_token = localStorage.getItem('oem_admin_token')
  }

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(admin_token ? { 'Admintoken': admin_token } : {}),
    ...(oem_admin_token ? { 'Oemtoken': oem_admin_token } : {}),
    ...options.headers,
  }

  let finalUrl = url
  if (options.params) {
    const query = new URLSearchParams()
    Object.entries(options.params).forEach(([key, value]) => {
      query.append(key, String(value))
    })
    finalUrl += `?${query.toString()}`
  }

  const { data, ...fetchOptions } = options
  if (data) {
    fetchOptions.body = JSON.stringify(data)
  }

  const handleUnauthorized = () => {
    if (!options.skip401Redirect) {
      toast.error('登录已过期，请重新登录')
      if (typeof window !== 'undefined') {
        const isAdmin = window.location.pathname.startsWith('/admin')
        const isOemAdmin = window.location.pathname.startsWith('/oem_admin')
        if (isAdmin) {
          window.location.href = '/admin/login'
        } else if (isOemAdmin) {
          window.location.href = '/oem_admin/login'
        } else {
          window.location.href = '/auth/login'
        }
      }
    }
    // Still throw to interrupt flow, but the redirect will happen
    throw new Error('Unauthorized')
  }

  try {
    const res = await fetch(finalUrl, {
      ...fetchOptions,
      headers,
    })

    if (res.status === 401) {
      handleUnauthorized()
    }

    // Handle non-JSON responses (like 500 HTML error pages from Next.js if uncaught)
    const contentType = res.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      const text = await res.text()
      console.error('Non-JSON response:', text)
      toast.error('服务器错误')
      const error = new Error('Server Error') as RequestError
      error.status = res.status
      error.code = res.status
      throw error
    }

    const responseData: ApiResponse<T> = await res.json()

    if (responseData.code !== 0) {
      if (responseData.code === 401) {
        handleUnauthorized()
      }
      if (!options.skipErrorToast && responseData.message !== 'INSUFFICIENT_BALANCE') {
        toast.error(responseData.message || '操作失败')
      }
      const error = new Error(responseData.message || 'Operation failed') as RequestError
      error.status = res.status
      error.code = responseData.code
      // 余额不足响应统一监听
      if (responseData.message === 'INSUFFICIENT_BALANCE') {
        sendBroadcastChannel(INSUFFICIENT_BALANCE_CHANNEL)
      }
      throw error
    }

    // 余额不足响应统一监听
    if ((responseData.data as any)?.error_code === 'INSUFFICIENT_BALANCE') {
      sendBroadcastChannel(INSUFFICIENT_BALANCE_CHANNEL)
    }

    return responseData.data
  } catch (error: any) {
    // If it's already handled (toast shown), just rethrow
    // If it's a network error, show toast
    if (error.message === 'Failed to fetch') {
      toast.error('网络连接失败')
    }
    throw error
  }
}
